import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useRouter } from "next/router";
import { Button, Grid, Paper } from "@material-ui/core";
import { createStyles, makeStyles } from "@material-ui/core/styles";
import { Save as SaveIcon } from "@material-ui/icons";
import { useListSubFormWithModalState } from "src/apps/commons/form/hooks";
import useHttpErrorHandler from "src/hooks/useHttpErrorHandler";
import { toDropdownOrNull } from "src/utils";
import { IPartnerBrief } from "../../partner/types";
import { IRemyndBrandBrief } from "../../remyndBrand/types";
import {
  IRemyndCategoryLv1Brief,
  IRemyndCategoryLv2Brief,
  IRemyndCategoryLv3Brief
} from "../../remyndCategory/types";
import { listMatching as listMatchingManuals } from "../../remyndManual/api";
import {
  IRemyndManualMatchingSearch,
  IRemyndManualSimple
} from "../../remyndManual/types";
import { IRemyndProduct } from "../../remyndProduct/types";
import { listMatching as listMatchingWarranty } from "../../remyndWarranty/api";
import {
  IRemyndWarrantyContainer,
  IRemyndWarrantyMatchingSearch
} from "../../remyndWarranty/types";
import {
  IRemyndVerifiedUserProductManualSaveReq,
  IRemyndVerifiedUserProductWarrantySaveReq,
  IUserProductContainer,
  IUserProductSaveReqContainer
} from "../types";
import { paths } from "../utils";
import RemyndProductPartner from "./RemyndProductPartner";
import UserInfo from "./UserInfo";
import UserProductInfo from "./UserProductInfo";
import UserProductManual from "./UserProductManual";
import UserProductManualModal from "./UserProductManualModal";
import UserProductWarranty from "./UserProductWarranty";
import UserProductWarrantyModal from "./UserProductWarrantyModal";

const useStyles = makeStyles(theme =>
  createStyles({
    root: {
      width: "100%",
      backgroundColor: theme.palette.background.default,
      marginTop: theme.spacing(2),
      padding: theme.spacing(2)
    },
    layoutButton: {
      flex: "1 1 auto"
    }
  })
);

interface Props {
  data: IUserProductContainer;
  submitForm: (req: IUserProductSaveReqContainer) => void;
}

export type EditHeaderState = PartialNull<{
  remyndBrandDropdown: IDropdown<IRemyndBrandBrief>;
  remyndCategoryLv1Dropdown: IDropdown<IRemyndCategoryLv1Brief>;
  remyndCategoryLv2Dropdown: IDropdown<IRemyndCategoryLv2Brief>;
  remyndCategoryLv3Dropdown: IDropdown<IRemyndCategoryLv3Brief>;
  remyndProductDropdown: IDropdown<IRemyndProduct>;
  partnerDropdown: IDropdown<IPartnerBrief>;
}>;

export type RemyndManualListState = {
  list: IRemyndManualSimple[];
  fetching: boolean;
};

export type RemyndWarrantyListState = {
  list: IRemyndWarrantyContainer[];
  fetching: boolean;
};

const Edit: React.FC<Props> = props => {
  const classes = useStyles();
  const router = useRouter();
  const { handleError } = useHttpErrorHandler();

  const { data, submitForm } = props;
  const { product } = data;

  const [editHeaderError, setEditHeaderError] =
    useState<{ message: string } | undefined>(undefined);
  const [editHeaderState, setEditHeaderState] = useState<EditHeaderState>({
    remyndBrandDropdown: toDropdownOrNull(product.remyndBrand),
    remyndCategoryLv1Dropdown: toDropdownOrNull(product.remyndCategoryLv1),
    remyndCategoryLv2Dropdown: toDropdownOrNull(product.remyndCategoryLv2),
    remyndCategoryLv3Dropdown: toDropdownOrNull(product.remyndCategoryLv3),
    remyndProductDropdown: toDropdownOrNull(product.remyndProduct),
    partnerDropdown: toDropdownOrNull(product.partner)
  });
  useEffect(() => {
    if (editHeaderState.remyndProductDropdown) {
      setEditHeaderError(undefined);
    }
  }, [editHeaderState.remyndProductDropdown]);

  const {
    delItem: delManual,
    items: savedManualList,
    modalState: manualModal,
    setModalState: setManualModal,
    closeModal: closeManualModal,
    submitModal: submitManualModal
  } = useListSubFormWithModalState<IRemyndVerifiedUserProductManualSaveReq>(
    data.savedManualList
  );
  const [manualNotify, setManualNotify] = useState(true); //default notify

  const [warrantyNotify, setWarrantyNotify] = useState(
    data.product.overrideUserWarranty ?? false
  );
  const [warrantyOverride, setWarrantyOverride] = useState(
    data.product.overrideUserWarranty ?? false // default not override
  );
  const handleMoveWarrantyUp = (idx: number) =>
    idx > 0 ? moveWarranty(idx, idx - 1) : undefined;
  const handleMoveWarrantyDown = (idx: number) =>
    idx < savedWarrantyList.length - 1 ? moveWarranty(idx, idx + 1) : undefined;

  const handleMapStateAndSubmit = () => {
    if (!editHeaderState.remyndProductDropdown) {
      // setMessage("error", "กรุณาระบุสินค้า (Remynd Product) ที่ Step 1");
      setEditHeaderError({ message: "กรุณาเลือกสินค้า" });
      return;
    }
    submitForm({
      header: {
        remyndBrandId: editHeaderState.remyndBrandDropdown?.value,
        remyndCategoryLv1Id: editHeaderState.remyndCategoryLv1Dropdown?.value,
        remyndCategoryLv2Id: editHeaderState.remyndCategoryLv2Dropdown?.value,
        remyndCategoryLv3Id: editHeaderState.remyndCategoryLv3Dropdown?.value,
        remyndProductId: editHeaderState.remyndProductDropdown?.value,
        partnerId: editHeaderState.partnerDropdown?.value,
        overrideUserWarranty: warrantyOverride
      },
      warrantyDetails: savedWarrantyList.map((d, idx) => ({ ...d, ord: idx })),
      warrantyDetailsNotify: { send: warrantyNotify },
      manuals: savedManualList,
      manualsNotify: { send: manualNotify }
    });
  };

  // ---- Master Manual ------------------------------------------------------

  const [remyndManualListState, setRemyndManualListState] =
    useState<RemyndManualListState>({
      list: data.matchingManualList,
      fetching: false
    });

  const fetchRemyndManuals = useCallback(
    async (search: IRemyndManualMatchingSearch) => {
      setRemyndManualListState({ list: [], fetching: true });
      try {
        const response = await listMatchingManuals(search);
        setRemyndManualListState({ list: response.value, fetching: false });
      } catch (e) {
        handleError(e);
        setRemyndManualListState({ list: [], fetching: false });
      }
    },
    [handleError]
  );

  const manualMatchingSearch = useMemo<
    IRemyndManualMatchingSearch | undefined
  >(() => {
    const productId = editHeaderState.remyndProductDropdown?.value;
    if (productId == null) {
      return undefined;
    }

    return {
      remyndProductId: productId,
      active: true
    };
  }, [editHeaderState.remyndProductDropdown?.value]);

  // ---- Master Warranty ----------------------------------------------------

  const [remyndWarrantyListState, setRemyndWarrantyListState] =
    useState<RemyndWarrantyListState>({
      list: data.matchingWarrantyList,
      fetching: false
    });

  const fetchRemyndWarranty = useCallback(
    async (search: IRemyndWarrantyMatchingSearch) => {
      setRemyndWarrantyListState({ list: [], fetching: true });
      try {
        const response = await listMatchingWarranty(search);
        setRemyndWarrantyListState({ list: response.value, fetching: false });
      } catch (e) {
        handleError(e);
        setRemyndWarrantyListState({ list: [], fetching: false });
      }
    },
    [handleError]
  );

  const defaultWarrantyList = useMemo<
    IRemyndVerifiedUserProductWarrantySaveReq[]
  >(
    () =>
      remyndWarrantyListState.list.length > 0
        ? remyndWarrantyListState.list[0].details.map(d => ({
            id: undefined,
            remyndWarrantyDetailId: d.id,
            ord: 0, // will be set at final submit
            active: true,
            startDate: undefined,
            endDate: undefined,
            labelTh: d.labelTh,
            labelEn: d.labelEn,
            type: d.type,
            years: d.years,
            months: d.months,
            days: d.days,
            remarkTh: d.remarkTh,
            remarkEn: d.remarkEn,
            fileId: undefined,
            snapLabelTh: d.labelTh,
            snapLabelEn: d.labelEn,
            snapType: d.type,
            snapYears: d.years,
            snapMonths: d.months,
            snapDays: d.days,
            snapRemarkTh: d.remarkTh,
            snapRemarkEn: d.remarkEn,
            snapEffectiveFrom: d.effectiveFrom,
            snapEffectiveUntil: d.effectiveUntil
          }))
        : [],
    [remyndWarrantyListState.list]
  );

  const {
    addItem: addWarranty,
    delItem: delWarranty,
    moveItem: moveWarranty,
    items: savedWarrantyList,
    modalState: warrantyModal,
    setModalState: setWarrantyModal,
    closeModal: closeWarrantyModal,
    submitModal: submitWarrantyModal
  } = useListSubFormWithModalState<IRemyndVerifiedUserProductWarrantySaveReq>(
    data.product.verified ? data.savedWarrantyList : defaultWarrantyList
  );

  const warrantyMatchingSearch = useMemo<
    IRemyndWarrantyMatchingSearch | undefined
  >(() => {
    const product = editHeaderState.remyndProductDropdown?.meta;
    const ddlBrandId = editHeaderState.remyndBrandDropdown?.value;

    const productId = product?.id;
    const brandId = product?.brand?.id ?? ddlBrandId;
    if (productId == null && brandId == null) {
      return undefined;
    }

    const ddlCatLv1Id = editHeaderState.remyndCategoryLv1Dropdown?.value;
    const ddlCatLv2Id = editHeaderState.remyndCategoryLv2Dropdown?.value;
    const ddlCatLv3Id = editHeaderState.remyndCategoryLv3Dropdown?.value;

    return {
      remyndProductId: productId,
      remyndBrandId: brandId,
      remyndCategoryLv1Id: product?.categoryLv1?.id ?? ddlCatLv1Id,
      remyndCategoryLv2Id: product?.categoryLv2?.id ?? ddlCatLv2Id,
      remyndCategoryLv3Id: product?.categoryLv3?.id ?? ddlCatLv3Id,
      limit: undefined,
      limitLevels: 1,
      active: true
    };
  }, [
    editHeaderState.remyndBrandDropdown?.value,
    editHeaderState.remyndCategoryLv1Dropdown?.value,
    editHeaderState.remyndCategoryLv2Dropdown?.value,
    editHeaderState.remyndCategoryLv3Dropdown?.value,
    editHeaderState.remyndProductDropdown
  ]);

  // ---- Fetch Masters ------------------------------------------------------

  useEffect(() => {
    if (manualMatchingSearch) {
      fetchRemyndManuals(manualMatchingSearch);
    } else {
      setRemyndManualListState({ list: [], fetching: false });
    }
  }, [fetchRemyndManuals, manualMatchingSearch]);

  useEffect(() => {
    if (warrantyMatchingSearch) {
      fetchRemyndWarranty(warrantyMatchingSearch);
    } else {
      setRemyndWarrantyListState({ list: [], fetching: false });
    }
  }, [fetchRemyndWarranty, warrantyMatchingSearch]);

  const disabledMessage =
    editHeaderState.remyndProductDropdown?.meta.partnerProduct != null
      ? "สินค้านี้ผูกกับ Partner Product แล้ว จึงไม่สามารถแก้ไขได้"
      : undefined;

  return (
    <>
      <UserInfo data={data} />
      <UserProductInfo data={data} />

      <RemyndProductPartner
        userProduct={data.product}
        state={editHeaderState}
        setState={setEditHeaderState}
        error={editHeaderError}
      />

      <UserProductWarranty
        userProduct={data.product}
        remyndBrand={editHeaderState.remyndBrandDropdown}
        remyndProduct={editHeaderState.remyndProductDropdown}
        remyndWarrantyListState={remyndWarrantyListState}
        userWarrantyList={data.userWarrantyList}
        savedWarrantyList={savedWarrantyList}
        addItem={addWarranty}
        delItem={delWarranty}
        moveItemUp={handleMoveWarrantyUp}
        moveItemDown={handleMoveWarrantyDown}
        setModal={setWarrantyModal}
        setWarrantyNotify={setWarrantyNotify}
        setWarrantyOverride={setWarrantyOverride}
        warrantyNotify={warrantyNotify}
        warrantyOverride={warrantyOverride}
        disabledMessage={disabledMessage}
      />

      <UserProductManual
        remyndProduct={editHeaderState.remyndProductDropdown}
        remyndManualListState={remyndManualListState}
        savedManualList={savedManualList}
        delItem={delManual}
        setModal={setManualModal}
        setManualNotify={setManualNotify}
        manualNotify={manualNotify}
        disabledMessage={disabledMessage}
      />

      <Paper className={classes.root}>
        <Grid container spacing={2}>
          <Grid item className={classes.layoutButton} />
          <Grid item>
            <Button
              variant="contained"
              onClick={() => router.push(paths.index)}
            >
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              variant="contained"
              color="primary"
              onClick={handleMapStateAndSubmit}
              startIcon={<SaveIcon />}
            >
              บันทึกข้อมูล
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {manualModal.open && (
        <UserProductManualModal
          label="คู่มือ/ไฟล์แนบ"
          open={manualModal.open}
          defaultValue={manualModal.item}
          handleCloseModal={closeManualModal}
          handleSubmitModal={submitManualModal}
        />
      )}
      {warrantyModal.open && (
        <UserProductWarrantyModal
          label="รายละเอียดการรับประกัน"
          userProduct={data.product}
          open={warrantyModal.open}
          defaultValue={warrantyModal.item}
          handleCloseModal={closeWarrantyModal}
          handleSubmitModal={submitWarrantyModal}
        />
      )}
    </>
  );
};

export default Edit;
